import { ReactNode } from "react";
import { Col, Form, Row } from "react-bootstrap";
import {
  recurrenceFrequencyOptions, recurrencePatternOptions, recurrenceEndModeOptions,
  dayOfWeekOptions, weekOfMonthOptions,
} from "../../utils/options";
import { RecurrenceEndMode, RecurrenceFrequency, RecurrencePattern } from "../../utils/enums";
import { getNowInTimezone } from "../../utils/helper";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/rootReducer";

// Every field is a Bootstrap col-4 (3 per row) inside one continuous Row, so
// Bootstrap's own grid - a fixed 33.33% width, not based on what's inside
// the column - handles the equal sizing and the wrapping once a row fills
// up. This avoids the issue custom CSS grid/flex widths kept running into:
// a native time-picker input doesn't shrink the way a dropdown does, so a
// content-based width made that one column wider than the others.
const Field = ({ children }: { children: ReactNode }) => (
  <Col md={4}>{children}</Col>
);

const RecurrenceFields = ({ formik }: { formik: any }) => {
  const { orgLocations, selectedLocationId } = useSelector((state: RootState) => state.user);
  const activeLocation = (orgLocations ?? []).find(
    (loc: any) => String(loc.id) === String(selectedLocationId)
  ) || (orgLocations && orgLocations.length > 0 ? orgLocations[0] : null);
  const activeTimezone = activeLocation?.location_timezone || 'UTC';

  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const browserTzRegion = browserTz.split('/').pop()?.replace(/_/g, ' ') || 'Local';

  return (
    <Row className="mt-3 g-3">
      <Field>
        <Form.Group className="form-group">
          <Form.Label>Frequency</Form.Label>
          <Form.Select
            name="recurrenceFrequency"
            value={formik.values.recurrenceFrequency}
            onChange={(e: any) => {
              formik.setFieldValue("recurrenceFrequency", e.target.value);
              formik.setFieldValue("recurrencePattern", "");
              formik.setFieldValue("recurrenceDayOfWeek", "");
              formik.setFieldValue("recurrenceDayOfMonth", "");
              formik.setFieldValue("recurrenceWeekOfMonth", "");
            }}
            isInvalid={!!formik.errors.recurrenceFrequency && !!formik.touched.recurrenceFrequency}
          >
            <option value="">Select</option>
            {recurrenceFrequencyOptions.map((v) => (
              <option key={v.key} value={v.value}>{v.text}</option>
            ))}
          </Form.Select>
          {formik.errors.recurrenceFrequency && (
            <Form.Control.Feedback type="invalid">{formik.errors.recurrenceFrequency as ReactNode}</Form.Control.Feedback>
          )}
        </Form.Group>
      </Field>

      {formik.values.recurrenceFrequency === RecurrenceFrequency.WEEKLY && (
        <Field>
          <Form.Group className="form-group">
            <Form.Label>Day of week</Form.Label>
            <Form.Select
              name="recurrenceDayOfWeek"
              value={formik.values.recurrenceDayOfWeek}
              onChange={(e: any) => formik.setFieldValue("recurrenceDayOfWeek", e.target.value)}
              isInvalid={!!formik.errors.recurrenceDayOfWeek && !!formik.touched.recurrenceDayOfWeek}
            >
              <option value="">Select</option>
              {dayOfWeekOptions.map((v) => (
                <option key={v.key} value={v.value}>{v.text}</option>
              ))}
            </Form.Select>
            {formik.errors.recurrenceDayOfWeek && (
              <Form.Control.Feedback type="invalid">{formik.errors.recurrenceDayOfWeek as ReactNode}</Form.Control.Feedback>
            )}
          </Form.Group>
        </Field>
      )}

      {formik.values.recurrenceFrequency === RecurrenceFrequency.MONTHLY && (
        <Field>
          <Form.Group className="form-group">
            <Form.Label>Monthly pattern</Form.Label>
            <Form.Select
              name="recurrencePattern"
              value={formik.values.recurrencePattern}
              onChange={(e: any) => {
                formik.setFieldValue("recurrencePattern", e.target.value);
                formik.setFieldValue("recurrenceDayOfMonth", "");
                formik.setFieldValue("recurrenceWeekOfMonth", "");
                formik.setFieldValue("recurrenceDayOfWeek", "");
              }}
              isInvalid={!!formik.errors.recurrencePattern && !!formik.touched.recurrencePattern}
            >
              <option value="">Select</option>
              {recurrencePatternOptions.map((v) => (
                <option key={v.key} value={v.value}>{v.text}</option>
              ))}
            </Form.Select>
            {formik.errors.recurrencePattern && (
              <Form.Control.Feedback type="invalid">{formik.errors.recurrencePattern as ReactNode}</Form.Control.Feedback>
            )}
          </Form.Group>
        </Field>
      )}

      {formik.values.recurrencePattern === RecurrencePattern.DAY_OF_MONTH && (
        <Field>
          <Form.Group className="form-group">
            <Form.Label>Day of month (1-31)</Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={31}
              name="recurrenceDayOfMonth"
              value={formik.values.recurrenceDayOfMonth}
              onChange={(e: any) => formik.setFieldValue("recurrenceDayOfMonth", e.target.value)}
              isInvalid={!!formik.errors.recurrenceDayOfMonth && !!formik.touched.recurrenceDayOfMonth}
            />
            {formik.errors.recurrenceDayOfMonth && (
              <Form.Control.Feedback type="invalid">{formik.errors.recurrenceDayOfMonth as ReactNode}</Form.Control.Feedback>
            )}
          </Form.Group>
        </Field>
      )}

      {formik.values.recurrencePattern === RecurrencePattern.NTH_WEEKDAY && (
        <>
          <Field>
            <Form.Group className="form-group">
              <Form.Label>Occurrence</Form.Label>
              <Form.Select
                name="recurrenceWeekOfMonth"
                value={formik.values.recurrenceWeekOfMonth}
                onChange={(e: any) => formik.setFieldValue("recurrenceWeekOfMonth", e.target.value)}
                isInvalid={!!formik.errors.recurrenceWeekOfMonth && !!formik.touched.recurrenceWeekOfMonth}
              >
                <option value="">Select</option>
                {weekOfMonthOptions.map((v) => (
                  <option key={v.key} value={v.value}>{v.text}</option>
                ))}
              </Form.Select>
              {formik.errors.recurrenceWeekOfMonth && (
                <Form.Control.Feedback type="invalid">{formik.errors.recurrenceWeekOfMonth as ReactNode}</Form.Control.Feedback>
              )}
            </Form.Group>
          </Field>
          <Field>
            <Form.Group className="form-group">
              <Form.Label>Weekday</Form.Label>
              <Form.Select
                name="recurrenceDayOfWeek"
                value={formik.values.recurrenceDayOfWeek}
                onChange={(e: any) => formik.setFieldValue("recurrenceDayOfWeek", e.target.value)}
                isInvalid={!!formik.errors.recurrenceDayOfWeek && !!formik.touched.recurrenceDayOfWeek}
              >
                <option value="">Select</option>
                {dayOfWeekOptions.map((v) => (
                  <option key={v.key} value={v.value}>{v.text}</option>
                ))}
              </Form.Select>
              {formik.errors.recurrenceDayOfWeek && (
                <Form.Control.Feedback type="invalid">{formik.errors.recurrenceDayOfWeek as ReactNode}</Form.Control.Feedback>
              )}
            </Form.Group>
          </Field>
        </>
      )}

      <Field>
        <Form.Group className="form-group">
          <Form.Label>Send time</Form.Label>
          <Form.Control
            type="time"
            name="recurrenceTime"
            value={formik.values.recurrenceTime}
            onChange={(e: any) => formik.setFieldValue("recurrenceTime", e.target.value)}
            isInvalid={!!formik.errors.recurrenceTime && !!formik.touched.recurrenceTime}
          />
          {formik.errors.recurrenceTime && (
            <Form.Control.Feedback type="invalid">{formik.errors.recurrenceTime as ReactNode}</Form.Control.Feedback>
          )}
        </Form.Group>
      </Field>

        {/* Time-zone selection field below {Temprorary removed} */}
      {/* <Field>
        <Form.Group className="form-group">
          <Form.Label>Timezone</Form.Label>
          <Form.Select
            name="recurrenceTimezone"
            value={formik.values.recurrenceTimezone}
            onChange={(e: any) => formik.setFieldValue("recurrenceTimezone", e.target.value)}
            isInvalid={!!formik.errors.recurrenceTimezone && !!formik.touched.recurrenceTimezone}
          >
            {timezoneOptionsWithDetected.map((v) => (
              <option key={v.key} value={v.value}>{v.text}</option>
            ))}
          </Form.Select>
          {formik.errors.recurrenceTimezone && (
            <Form.Control.Feedback type="invalid">{formik.errors.recurrenceTimezone as ReactNode}</Form.Control.Feedback>
          )}
        </Form.Group>
      </Field> */}

      <Field>
        <Form.Group controlId="formBasicRecurrenceEndMode" className='d-flex align-items-center custom-radio-btns gap-3 h-100'>
          {recurrenceEndModeOptions.map((v) => (
            <Form.Check
              key={v.key}
              type="radio"
              label={v.text}
              className="d-flex align-items-center"
              name="recurrenceEndMode"
              id={`recurrence_end_mode_${v.key}`}
              checked={formik.values.recurrenceEndMode === v.value}
              onChange={() => formik.setFieldValue("recurrenceEndMode", v.value)}
            />
          ))}
        </Form.Group>
      </Field>

      {formik.values.recurrenceEndMode === RecurrenceEndMode.END_DATE && (
        <Field>
          <Form.Group className="form-group">
            <Form.Label>End date</Form.Label>
            <Form.Control
              type="datetime-local"
              name="recurrenceEndDate"
              value={formik.values.recurrenceEndDate}
              onChange={(e: any) => formik.setFieldValue("recurrenceEndDate", e.target.value)}
              min={getNowInTimezone(activeTimezone)}
              isInvalid={!!formik.errors.recurrenceEndDate && !!formik.touched.recurrenceEndDate}
            />
            <div className="text-muted mt-1" style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>
              Your local time ({browserTzRegion}): {new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, month: 'short', day: 'numeric' }).format(new Date())}
            </div>
            {formik.errors.recurrenceEndDate && (
              <Form.Control.Feedback type="invalid">{formik.errors.recurrenceEndDate as ReactNode}</Form.Control.Feedback>
            )}
          </Form.Group>
        </Field>
      )}
    </Row>
  );
};

export default RecurrenceFields;
